import { motion } from "framer-motion";
import { ChevronRight, Zap, Shield, Trophy, Loader2 } from "lucide-react";
import { useArcConnect, safeWalletClick } from "@/context/ArcEcosystemContext";

const HERO_COIN =
  "https://static.prod-images.emergentagent.com/jobs/756805e3-0bfe-493b-a89d-5ce6463cebd2/images/d3a4dace2efb875fed4a83dfb2762b45d8e0ab272c085d55f1411ae10ea0a329.png";

const ARENA_BG =
  "https://static.prod-images.emergentagent.com/jobs/756805e3-0bfe-493b-a89d-5ce6463cebd2/images/d7df98da4c1022d13e616e4c91e26714eafd13dd5f71301bc23f372ef4306f19.png";

export default function Hero() {
  const { connect, connecting, isConnected } = useArcConnect();
  const heroLabel = connecting ? "Connecting…" : isConnected ? "Wallet Connected" : "Connect Wallet";
  return (
    <section id="top" className="relative overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${ARENA_BG})` }}
        />
        <div className="absolute inset-0 spotlight" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-[#39FF14]/40 bg-[#39FF14]/5"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-dot" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
                Presale Round 2 · Live Now
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]"
              data-testid="hero-headline"
            >
              The currency of the <span className="shine-text">PvP arena</span>.<br />
              No house. No edge. <span className="glow-cyan text-[#00F0FF]">Just the pool.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed font-mono"
            >
              Arenacoin (ARC) powers Arena Platform — a suite of
              PvP multiplayer betting tournaments where players stake an entry fee, compete in skill
              brackets, and split a prize pool. Operator commission is locked at 8% (7% if the
              creator wins). Fixed supply: <span className="text-[#00F0FF]">100M ARC on BNB Smart Chain</span>. No house liability. Pure player-vs-player economics.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <a href="#presale" className="btn-primary inline-flex items-center gap-2" data-testid="hero-buy-arc-button">
                Buy ARC Now <ChevronRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => safeWalletClick(connect)}
                disabled={connecting || isConnected}
                className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="hero-connect-wallet-button"
                data-connect-state={connecting ? "connecting" : isConnected ? "connected" : "disconnected"}
              >
                {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
                {heroLabel}
              </button>
              <a href="#about" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-whitepaper-button">
                Whitepaper
              </a>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
              data-testid="hero-trust-strip"
            >
              <div className="flex items-center gap-2 text-white/70">
                <Shield className="w-4 h-4 text-[#39FF14]" />
                <span className="font-mono text-xs uppercase tracking-widest">Audited Contract</span>
              </div>
              <div className="flex items-center gap-2 text-white/70">
                <Zap className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono text-xs uppercase tracking-widest">BEP-20 · BSC</span>
              </div>
              <div className="flex items-center gap-2 text-white/70">
                <Trophy className="w-4 h-4 text-[#FF3B30]" />
                <span className="font-mono text-xs uppercase tracking-widest">Skill-Based PvP</span>
              </div>
            </motion.div>
          </div>

          {/* Right: 3D coin */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative mx-auto max-w-md"
            >
              <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(57,255,20,0.35),transparent_60%)] blur-2xl" />
              <img
                src={HERO_COIN}
                alt="Arenacoin ARC 3D medallion"
                className="coin-float w-full h-auto select-none"
                draggable={false}
                data-testid="hero-coin-image"
              />
              <div className="absolute -inset-x-12 -bottom-6 h-24 bg-gradient-to-t from-[#050505] to-transparent" />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="divider-line" />
    </section>
  );
}
